import { useRouteError } from 'react-router-dom'
import ErrorPage from '../ErrorPage'
import { Button, Card, CardBody } from '@vnetwork/datacentral-ui/primitives'
import { EmptyState } from '@vnetwork/datacentral-ui/data'
import { useTranslation } from 'react-i18next'
import { AlertError } from '@/components/AlertError'

// Define generic error types that can be thrown from route loaders
interface RouteError extends Error {
    status?: number
    statusText?: string
    data?: any
    errorTitle?: string
    resourceType?: string
    originalError?: any
}

// Type guard to check if error is a RouteError
const isRouteError = (error: any): error is RouteError => {
    return error && typeof error === 'object' && 'message' in error
}

// Type guard to check if error is a Response-like object
const isResponseError = (error: any): error is Response => {
    return (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        'statusText' in error
    )
}

const ErrorPageWithRoute = () => {
    const routeError = useRouteError()
    const { t } = useTranslation()

    // If no error provided, fallback to default ErrorPage
    if (!routeError) {
        return <ErrorPage status={500} />
    }

    // Extract error information based on error type
    let status: number
    let error: Error
    let errorTitle: string | undefined
    let resourceType: string | undefined

    if (isRouteError(routeError)) {
        // Handle custom route errors (from loaders)
        status = routeError.status || 500
        error = routeError
        errorTitle = routeError.errorTitle
        resourceType = routeError.resourceType
    } else if (isResponseError(routeError)) {
        // Handle Response errors
        status = routeError.status
        error = new Error(routeError.statusText || `HTTP ${status} Error`)
    } else if (typeof routeError === 'string') {
        // Handle string errors
        status = 500
        error = new Error(routeError)
    } else {
        // Handle unknown error types
        status = 500
        error = new Error('An unexpected error occurred')
    }

    // Create enhanced error object for display
    const enhancedError = error as Error & {
        status?: number
        errorTitle?: string
        resourceType?: string
    }

    enhancedError.status = status
    if (errorTitle) enhancedError.errorTitle = errorTitle
    if (resourceType) enhancedError.resourceType = resourceType

    const description =
        status === 403
            ? `You don't have permission to access this ${resourceType || 'resource'}. Please contact the ${resourceType || 'resource'} owner or administrator for access.`
            : status === 404
              ? `The requested ${resourceType || 'resource'} could not be found. It may have been moved or deleted.`
              : status === 401
                ? `You need to be logged in to access this ${resourceType || 'resource'}. Please log in and try again.`
                : status === 500
                  ? 'An internal server error occurred. Please try again later or contact support if the problem persists.'
                  : `We seem to have encountered an issue accessing your ${resourceType || 'resource'}. Please try again or contact support if the problem persists.`

    return (
        <Card>
            <CardBody className="space-y-4">
                {error && <AlertError error={error} />}
                <EmptyState
                    title={`${status}${errorTitle ? ` · ${errorTitle}` : ''}`}
                    description={description}
                    actions={
                        <>
                            <Button
                                size="sm"
                                type="button"
                                onClick={() => window.history.back()}
                            >
                                Go Back
                            </Button>
                            <Button
                                size="sm"
                                type="button"
                                variant="secondary"
                                onClick={() => window.location.reload()}
                            >
                                {t('reload', 'Reload')}
                            </Button>
                        </>
                    }
                />
            </CardBody>
        </Card>
    )
}

export default ErrorPageWithRoute
